import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AppHeader } from "@/components/AppHeader";
import { AttributeLeaders } from "@/components/AttributeLeaders";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { SPORTS, fmtStat, statLabel } from "@/constants/sports";
import { api } from "@/lib/api";

const Board = ({ sport, stat, leaders }) => (
  <section data-testid={`leaderboard-${stat}`} className="rise overflow-hidden rounded-xl border border-slate-800 bg-[#131927]">
    <div className="flex items-baseline justify-between border-b border-slate-800 px-5 py-4">
      <h2 className="font-display text-2xl font-extrabold uppercase tracking-tight">{statLabel(sport, stat)}</h2>
      <span className="eyebrow">{stat === "xg_per_game" ? "Min 3 games" : "Season"}</span>
    </div>
    {!leaders.length && <p className="p-5 text-sm text-slate-500">No leaders yet.</p>}
    <ol>
      {leaders.map((p, i) => (
        <li key={p.id}>
          <Link to={`/players/${p.id}`} data-testid={`leader-${stat}-${i + 1}`}
            className={`flex items-center gap-4 px-5 py-3 transition-colors duration-200 hover:bg-[#0B0F19]/70 ${i === 0 ? "bg-[#FF2D78]/10" : ""}`}>
            <span className={`w-6 font-display text-2xl font-black ${i === 0 ? "text-[#FF2D78]" : "text-slate-600"}`}>{i + 1}</span>
            {p.team_logo ? <img src={p.team_logo} alt="" className="h-7 w-7 shrink-0 object-contain" /> : <span className="h-7 w-7 shrink-0" />}
            <span className="min-w-0 flex-1">
              <span className="block truncate font-semibold text-white">{p.name}</span>
              <span className="block truncate text-xs text-slate-500">{[p.team, p.position].filter(Boolean).join(" · ")}</span>
            </span>
            <span className={`font-data text-xl font-bold ${i === 0 ? "text-white" : "text-slate-200"}`}>{fmtStat(stat, p.stats[stat])}</span>
          </Link>
        </li>
      ))}
    </ol>
  </section>
);

export default function LeadersPage() {
  const [mode, setMode] = useState("stats");
  const [sport, setSport] = useState("volleyball");
  const [conference, setConference] = useState("all");
  const [data, setData] = useState(null);

  useEffect(() => {
    let stale = false;
    setData(null);
    api.get("/leaderboards", { params: { sport, ...(conference !== "all" ? { conference } : {}) } })
      .then((r) => { if (!stale) setData(r.data); });
    return () => { stale = true; };
  }, [sport, conference]);

  return (
    <div className="min-h-screen bg-[#0B0F19]">
      <AppHeader />
      <main className="mx-auto max-w-[1400px] px-4 pb-24 pt-10 sm:px-8">
        <p className="eyebrow !text-[#FF2D78]">Season leaders</p>
        <h1 className="mt-3 font-display text-4xl font-black uppercase tracking-tight sm:text-5xl lg:text-6xl">Who's leading.</h1>
        <div className="mt-8 flex gap-6 border-b border-slate-800">
          {[["stats", "Season stats"], ["attributes", "Attributes"]].map(([k, l]) => (
            <button key={k} data-testid={`leaders-mode-${k}`} onClick={() => setMode(k)}
              className={`-mb-px border-b-2 pb-3 font-data text-xs uppercase tracking-widest transition-colors duration-200 ${mode === k ? "border-[#FF2D78] text-white" : "border-transparent text-slate-500 hover:text-white"}`}>{l}</button>
          ))}
        </div>
        {mode === "attributes" ? <div className="mt-8"><AttributeLeaders /></div> : <>
        <div className="mt-10 flex flex-wrap items-center gap-4">
          <div className="grid grid-cols-2 gap-1 rounded-lg border border-slate-800 bg-[#131927] p-1">
            {Object.entries(SPORTS).filter(([, s]) => !s.rosterOnly).map(([k, s]) => (
              <button key={k} data-testid={`leaders-sport-${k}`} onClick={() => { setSport(k); setConference("all"); }}
                className={`rounded-md px-5 py-2 font-data text-[11px] uppercase tracking-wider transition-colors duration-200 ${sport === k ? "bg-[#FF2D78] text-white" : "text-slate-400 hover:text-white"}`}>
                {s.label}
              </button>
            ))}
            <span data-testid="leaders-sport-softball-soon" className="col-span-2 px-5 py-1 text-center font-data text-[10px] uppercase tracking-wider text-slate-500">Softball & basketball: see Attributes · lacrosse coming soon</span>
          </div>
          <Select value={conference} onValueChange={setConference}>
            <SelectTrigger data-testid="leaders-conference-select" className="h-11 w-64 border-slate-800 bg-[#131927]"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-80 border-slate-800 bg-[#131927]">
              <SelectItem value="all">All conferences</SelectItem>
              {(data?.conferences || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="mt-10 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
          {!data && [...Array(3)].map((_, i) => <Skeleton key={i} className="h-[520px] rounded-xl bg-[#131927]" />)}
          {data?.boards.map((b) => <Board key={b.stat} sport={sport} stat={b.stat} leaders={b.leaders} />)}
        </div></>}
      </main>
    </div>
  );
}
